import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ExternalLink,
  Loader2,
  Save,
  Truck,
  X,
} from 'lucide-react';
import apiService from '../services/api';
import Modal from './Modal';
import LivraisonCreneauxManager from './LivraisonCreneauxManager';
import { formatLivraisonPlageLabel } from '../utils/livraisonCommandeDisplay';
import {
  buildLivraisonCreneauxCandidatesFromHoraires,
  partitionLivraisonPrefill,
} from '../utils/livraisonPrefillFromHoraires';

const JOUR_OPTIONS = [
  { value: 1, label: 'Lundi' },
  { value: 2, label: 'Mardi' },
  { value: 3, label: 'Mercredi' },
  { value: 4, label: 'Jeudi' },
  { value: 5, label: 'Vendredi' },
  { value: 6, label: 'Samedi' },
  { value: 0, label: 'Dimanche' },
];

function capacitePreviewLabel(capacite) {
  if (capacite == null) return 'Illimité';
  const n = Number(capacite);
  if (!Number.isInteger(n) || n < 1) return 'Illimité';
  return `Capacité : ${n}`;
}

function groupByDay(creneaux) {
  const map = {};
  JOUR_OPTIONS.forEach((day) => {
    map[day.value] = [];
  });
  (creneaux || []).forEach((item) => {
    if (!map[item.jourSemaine]) map[item.jourSemaine] = [];
    map[item.jourSemaine].push(item);
  });
  Object.keys(map).forEach((key) => {
    map[key].sort((a, b) => String(a.heureDebut).localeCompare(String(b.heureDebut)));
  });
  return map;
}

const LivraisonManager = ({
  commercant,
  onUpdate,
  loading = false,
  onNavigateToTab,
}) => {
  const [livraisonAsap, setLivraisonAsap] = useState(commercant?.livraisonAsap !== false);
  const [livraisonCreneaux, setLivraisonCreneaux] = useState(
    Boolean(commercant?.livraisonCreneaux),
  );
  const [hasModeChanges, setHasModeChanges] = useState(false);
  const [creneaux, setCreneaux] = useState([]);
  const [reloadToken, setReloadToken] = useState(0);
  const [prefillOpen, setPrefillOpen] = useState(false);
  const [prefillCandidates, setPrefillCandidates] = useState([]);
  const [prefillSkippedPreview, setPrefillSkippedPreview] = useState(0);
  const [importing, setImporting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setLivraisonAsap(commercant?.livraisonAsap !== false);
    setLivraisonCreneaux(Boolean(commercant?.livraisonCreneaux));
    setHasModeChanges(false);
  }, [commercant?.id, commercant?.livraisonAsap, commercant?.livraisonCreneaux]);

  const modeError =
    !livraisonAsap && !livraisonCreneaux
      ? 'Si la livraison locale est activée, choisissez « Dès que possible » et/ou « Livraison sur créneaux ».'
      : '';

  const previewByDay = useMemo(() => groupByDay(creneaux), [creneaux]);
  const hasAnyCreneau = creneaux.length > 0;

  const handleModeToggle = (field) => {
    if (field === 'livraisonAsap') {
      setLivraisonAsap((prev) => !prev);
    } else {
      setLivraisonCreneaux((prev) => !prev);
    }
    setHasModeChanges(true);
  };

  const handleCancelModes = () => {
    setLivraisonAsap(commercant?.livraisonAsap !== false);
    setLivraisonCreneaux(Boolean(commercant?.livraisonCreneaux));
    setHasModeChanges(false);
  };

  const handleSaveModes = async () => {
    if (modeError) return;
    try {
      setError('');
      await onUpdate({
        livraisonAsap,
        livraisonCreneaux,
      });
      setHasModeChanges(false);
    } catch (err) {
      setError(err?.message || 'Impossible d’enregistrer les modes de livraison.');
    }
  };

  const openPrefillPreview = () => {
    const candidates = buildLivraisonCreneauxCandidatesFromHoraires(commercant?.horaires);
    const { toAdd, skipped } = partitionLivraisonPrefill(candidates, creneaux);
    setPrefillCandidates(toAdd);
    setPrefillSkippedPreview(skipped);
    setPrefillOpen(true);
    setError('');
  };

  const confirmPrefill = async () => {
    if (!commercant?.id) return;
    try {
      setImporting(true);
      setError('');
      let added = 0;
      let skipped = prefillSkippedPreview;
      for (const candidate of prefillCandidates) {
        try {
          await apiService.createLivraisonCreneau(commercant.id, candidate);
          added += 1;
        } catch (err) {
          const msg = String(err?.message || '');
          // Doublon concurrent éventuel → compter comme skip, ne pas bloquer le lot.
          if (/déjà|existe|doublon|overlap|chevauch/i.test(msg)) {
            skipped += 1;
          } else {
            throw err;
          }
        }
      }
      setPrefillOpen(false);
      setFeedback(
        `${added} créneau${added > 1 ? 'x' : ''} ajouté${added > 1 ? 's' : ''}${
          skipped > 0 ? `, ${skipped} déjà existant${skipped > 1 ? 's' : ''}` : ''
        }.`,
      );
      setReloadToken((n) => n + 1);
    } catch (err) {
      setError(err?.message || 'Impossible de préremplir les créneaux.');
    } finally {
      setImporting(false);
      setTimeout(() => setFeedback(''), 5000);
    }
  };

  const prefillPreviewByDay = useMemo(
    () => groupByDay(prefillCandidates),
    [prefillCandidates],
  );

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <div className="rounded-lg bg-blue-100 p-2">
            <Truck className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Livraison</h3>
            <p className="text-sm text-gray-500">
              Modes de livraison et créneaux proposés aux clients
            </p>
          </div>
        </div>

        {(error || modeError) && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="flex items-start gap-2 text-sm text-red-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error || modeError}</span>
            </div>
          </div>
        )}
        {feedback && (
          <div className="mb-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            {feedback}
          </div>
        )}

        <section className="space-y-3">
          <div>
            <h4 className="text-base font-semibold text-gray-900">Mode de livraison</h4>
            <p className="mt-1 text-sm text-gray-500">
              Choisissez comment vos clients peuvent recevoir leurs commandes.
            </p>
          </div>

          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={livraisonAsap}
              onChange={() => handleModeToggle('livraisonAsap')}
              className="mt-0.5 rounded border-gray-300 text-blue-600"
            />
            <span>
              <span className="font-medium">Dès que possible</span>
              <span className="mt-0.5 block text-xs text-gray-500">
                Le client ne choisit pas d&apos;horaire. Estimation selon vos délais de zone.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={livraisonCreneaux}
              onChange={() => handleModeToggle('livraisonCreneaux')}
              className="mt-0.5 rounded border-gray-300 text-blue-600"
            />
            <span>
              <span className="font-medium">Livraison sur créneaux</span>
              <span className="mt-0.5 block text-xs text-gray-500">
                Le client choisit une plage complète définie ci-dessous.
              </span>
            </span>
          </label>

          {hasModeChanges && (
            <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={handleCancelModes}
                className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700"
              >
                <X className="h-4 w-4" />
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSaveModes}
                disabled={loading || Boolean(modeError)}
                className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer
              </button>
            </div>
          )}
        </section>
      </div>

      {livraisonCreneaux && (
        <>
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h4 className="text-base font-semibold text-gray-900">Prévisualisation</h4>
                <p className="mt-1 text-sm text-gray-500">
                  Aperçu des créneaux proposés aux clients (fenêtres complètes).
                </p>
              </div>
              <button
                type="button"
                onClick={openPrefillPreview}
                className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
              >
                Préremplir depuis les horaires
              </button>
            </div>

            {!hasAnyCreneau ? (
              <p className="text-sm text-gray-400">Aucun créneau configuré pour le moment.</p>
            ) : (
              <div className="space-y-4">
                {JOUR_OPTIONS.map((day) => {
                  const items = previewByDay[day.value] || [];
                  if (!items.length) return null;
                  return (
                    <div key={day.value}>
                      <h5 className="text-sm font-medium text-gray-900">{day.label}</h5>
                      <ul className="mt-1 space-y-1">
                        {items.map((creneau) => (
                          <li key={creneau.id} className="text-sm text-gray-700">
                            {formatLivraisonPlageLabel(creneau.heureDebut, creneau.heureFin) ||
                              `${creneau.heureDebut} – ${creneau.heureFin}`}
                            <span className="ml-2 text-xs text-gray-500">
                              {capacitePreviewLabel(creneau.capacite)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {commercant?.id && (
            <LivraisonCreneauxManager
              commercantId={commercant.id}
              reloadToken={reloadToken}
              onCreneauxChange={setCreneaux}
            />
          )}
        </>
      )}

      <div className="flex flex-wrap gap-4 text-sm">
        {onNavigateToTab && (
          <>
            <button
              type="button"
              onClick={() => onNavigateToTab('zones')}
              className="inline-flex items-center gap-1.5 font-medium text-indigo-700 hover:text-indigo-900"
            >
              <ExternalLink className="h-4 w-4" />
              Gérer les zones de livraison
            </button>
            <button
              type="button"
              onClick={() => onNavigateToTab('livreurs')}
              className="inline-flex items-center gap-1.5 font-medium text-indigo-700 hover:text-indigo-900"
            >
              <ExternalLink className="h-4 w-4" />
              Gérer les livreurs
            </button>
          </>
        )}
      </div>

      <Modal
        isOpen={prefillOpen}
        onClose={() => !importing && setPrefillOpen(false)}
        title="Créneaux qui seront ajoutés"
        maxWidth="max-w-lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Ces créneaux serviront uniquement de base. Vous pourrez ensuite les modifier
            indépendamment des horaires de votre établissement.
          </p>

          {prefillCandidates.length === 0 ? (
            <p className="text-sm text-gray-500">
              Aucun nouveau créneau à ajouter
              {prefillSkippedPreview > 0
                ? ` (${prefillSkippedPreview} déjà existant${prefillSkippedPreview > 1 ? 's' : ''}).`
                : ' (vérifiez vos horaires d’ouverture).'}
            </p>
          ) : (
            <div className="max-h-72 space-y-3 overflow-y-auto rounded-md border border-gray-100 p-3">
              {JOUR_OPTIONS.map((day) => {
                const items = prefillPreviewByDay[day.value] || [];
                if (!items.length) return null;
                return (
                  <div key={day.value}>
                    <h5 className="text-sm font-medium text-gray-900">{day.label}</h5>
                    <ul className="mt-1 space-y-0.5 text-sm text-gray-700">
                      {items.map((c) => (
                        <li key={`${c.jourSemaine}-${c.heureDebut}-${c.heureFin}`}>
                          {formatLivraisonPlageLabel(c.heureDebut, c.heureFin) ||
                            `${c.heureDebut} – ${c.heureFin}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}

          {prefillSkippedPreview > 0 && prefillCandidates.length > 0 && (
            <p className="text-xs text-gray-500">
              {prefillSkippedPreview} déjà existant
              {prefillSkippedPreview > 1 ? 's' : ''} — ignoré
              {prefillSkippedPreview > 1 ? 's' : ''} (ajout uniquement).
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              disabled={importing}
              onClick={() => setPrefillOpen(false)}
              className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700"
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={importing || prefillCandidates.length === 0}
              onClick={confirmPrefill}
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Ajouter les créneaux
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LivraisonManager;
